import React, { useMemo, useState } from "react";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import {
  DataTable,
  DataTableStateEvent,
  SortOrder,
} from "primereact/datatable";
import { Column } from "primereact/column";
import Plus from "@components/icones/plus.tsx";
import CustomDialog from "@shared/customDialog.tsx";
import { UserDTO } from "../user.model.ts";
import Spinner from "@shared/Spinner.tsx";
import { PaginatorCurrentPageReportOptions } from "primereact/paginator";
import { useAppDispatch, useAppSelector } from "@core/store/hook.ts";
import { getAccess, getAllUsers, getProfileList, deleteUser } from "@core/actions/usersActions.ts";
import { createUserDeletionRequest } from "@core/actions/userDeletionRequestActions.ts";
import {
  access,
  loadingDeleteUser,
  loadingUser,
  profileList,
  setUserDetails,
  userConnected,
  userFiltered,
  userLists,
} from "@core/slice/UsersSlice.ts";
import { FetchStatusType } from "@core/modeles/model.ts";
import Message from "@shared/message/Message.tsx";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { DropDownList, TypeProfil } from "@modules/plaintes/plaintes.model.ts";
import { userDeletionRequestService } from "@modules/setting/userManagement/userDeletionRequest.service.ts";

// Utilitaire pour extraire l'uuid string
const getUuidString = (uuid: string | { value: string }) =>
  typeof uuid === 'object' && uuid !== null ? uuid.value : uuid;

function useDeletionRequests(
  enabled: boolean,
  page: number,
  pageSize: number,
  order: string,
  orderDir: string,
  searchTerm: string,
  idPermission: string,
  idAccess: string,
  localisation: string,
  reload: number
) {
  const [data, setData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    userDeletionRequestService.getRequests({
      page: page, // page doit être 1-based, donc deletePage + 1 dans l'appel du hook
      size: pageSize,
      sortBy: order,
      sortDir: orderDir,
      search: searchTerm,
      access: idAccess,
      localisation,
      profil: idPermission,
    })
      .then((res: any) => {
        // Si la réponse est un array, on l'encapsule dans { data, total }
        if (Array.isArray(res)) {
          setData({ data: res, total: res.length });
        } else if (res && Array.isArray(res.data)) {
          setData({ data: res.data, total: res.total ?? res.data.length });
        } else {
          setData({ data: [], total: 0 });
        }
      })
      .catch(() => setData({ data: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [enabled, page, pageSize, order, orderDir, searchTerm, idPermission, idAccess, localisation, reload]);

  return { data, loading };
}


interface ActionDeleteRequestTemplateProps {
  rowData: any;
  onRefresh: () => void;
}

const ActionDeleteRequestTemplate: React.FC<ActionDeleteRequestTemplateProps> = ({ rowData, onRefresh }) => {
  const dispatch = useAppDispatch();
  const detailsUserConnected = useAppSelector(userConnected);
  const [open, setOpen] = React.useState(false);
  const [validating, setValidating] = React.useState(false);
  const [messageInfo, setMessageInfo] = React.useState<{
    severity: "success" | "info" | "warn" | "error";
    summary: string;
    detail: string;
  }>({
    severity: "success",
    summary: "",
    detail: "",
  });

  const showSuccessMessage = () => {
    setMessageInfo({
      severity: "success",
      summary: "Succès",
      detail: "Suppression de l'utilisateur réussie !",
    });
    setOpen(true);
  };
  const showErrorMessage = (message?: string) => {
    setMessageInfo({
      severity: "error",
      summary: "Erreur",
      detail: message ? message : "Une erreur s'est produite, veuillez réessayer !",
    });
    setOpen(true);
  };

  const handleValidate = async () => {
    try {
      setValidating(true);
      const requestId = rowData.id;
      const response = await userDeletionRequestService.getDeleteRequestById(requestId);
      const uuidConnected = typeof detailsUserConnected?.uuid === 'object'
        ? (detailsUserConnected.uuid as { value: string }).value
        : detailsUserConnected?.uuid;
      if (uuidConnected === response.requestBy?.uuid) {
        showErrorMessage("Vous n'avez pas le droit à cette action car vous êtes l'initiateur.");
        setValidating(false);
        return;
      }
      dispatch(
        deleteUser({
          id: rowData.userId || rowData.uuid || rowData.id,
          onSuccess: () => {
            showSuccessMessage();
            setValidating(false);
            setTimeout(() => {
              onRefresh();
            }, 1200); 
          },
          onError: () => {
            showErrorMessage();
            setValidating(false);
          },
        })
      );
    } catch (e) {
      showErrorMessage();
      setValidating(false);
    }
  };

  return (
    <div className="flex items-center space-x-1">
      <Button
        icon={<i className="pi pi-eye text-xl text-green-700" />}
        className="bg-transparent hover:bg-green-100 rounded-full p-2"
        tooltip="Voir"
        tooltipOptions={{ position: "left" }}
        onClick={() => {
          if (rowData.userId || rowData.uuid || rowData.id) {
            window.location.href = `/setting/view-detail/${rowData.userId || rowData.uuid || rowData.id}`;
          }
        }}
      />
      <Button
        icon={validating
          ? <div className="w-4 h-4 rounded-full animate-spin border-y-4 border-solid border-green-700 border-t-transparent"></div>
          : <i className="pi pi-check text-xl text-green-700" />
        }
        className="bg-transparent hover:bg-green-100 rounded-full p-2"
        tooltip={"Valider"}
        tooltipOptions={{ position: "left" }}
        disabled={validating}
        onClick={handleValidate}
      />
      <Message
        open={open}
        severity={messageInfo.severity}
        summary={messageInfo.summary}
        detail={messageInfo.detail}
      />
    </div>
  );
};


const ListingUser: React.FC = () => {
  const [reload, setReload] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'users' | 'delete'>('users');
  const [deletePage, setDeletePage] = useState(0);
  const [deletePageSize, setDeletePageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [order, setOrder] = useState<string>("firstName");
  const [orderDir, setOrderDir] = useState<SortOrder>(-1);
  const [idPermission, setIdPermission] = useState<string>("");
  const [idAccess, setIdAccess] = useState<string>("");
  const [localisation, setLocalisation] = useState<string>("");
  const { data: deleteRequests, loading: loadingDeleteRequests } = useDeletionRequests(
    activeTab === 'delete',
    deletePage + 1,
    deletePageSize,
    order,
    orderDir === 1 ? 'asc' : 'desc',
    searchTerm,
    idPermission,
    idAccess,
    localisation,
    reload
  );
    if (process.env.NODE_ENV === 'development') {
      console.log('deleteRequests:', deleteRequests);
    }
  // Pagination template pour demandes de suppression
  const deletePaginatorTemplate = useMemo(() => {
    return {
      layout:
        "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
      CurrentPageReport: ({ rows }: PaginatorCurrentPageReportOptions) => {
        const page = deletePage + 1;
        const totalRecords = typeof (deleteRequests as any)?.total === 'number'
          ? (deleteRequests as any).total
          : Array.isArray(deleteRequests)
            ? deleteRequests.length
            : 0;
        const first = page * rows + 1 - rows;
        const last = Math.min(first + rows - 1, totalRecords);
        return (
          <span>
            Afficher {first} à {last} sur {totalRecords} lignes
          </span>
        );
      },
    };
  }, [deleteRequests, deletePage]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const users = useAppSelector(userLists);
  const profiles = useAppSelector(profileList);
  const userListFiltered = useAppSelector(userFiltered);
  const isLoading = useAppSelector(loadingUser);
  const loadingDelete = useAppSelector(loadingDeleteUser);
  const listAccess = useAppSelector(access);
  const detailsUserConnected = useAppSelector(userConnected);

  const [visibleConfirmDialog, setVisibleConfirmDialog] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [userDelete, setUserDelete] = useState<UserDTO>();
  const [open, setOpen] = useState(false);
  const [roleUser, setRoleUser] = useState<DropDownList>();
  const [accessUser, setAccessUser] = useState<DropDownList>();
  const [messageInfo, setMessageInfo] = useState<{
    severity: "success" | "info" | "warn" | "error";
    summary: string;
    detail: string;
  }>({
    severity: "success",
    summary: "",
    detail: "",
  });

  const isFilter = reload > 0 || idAccess.length > 0 || idPermission.length > 0;
  const isCompetentEntity =
    detailsUserConnected?.profil.type === TypeProfil.COMPETENT_ENTITY;

  React.useEffect(() => {
    dispatch(
      getProfileList({
        page: 1,
        size: 1000,
        search: "",
        sortBy: "desc",
        sortDir: "",
      })
    );
    dispatch(getAccess());
    dispatch(
      getAllUsers({
        page: 1,
        size: 10,
        sortBy: "firstName",
        sortDir: "desc",
        search: "",
        access: "",
        localisation: "",
        profil: "",
      })
    );
    dispatch(setUserDetails(null));
  }, [dispatch]);


  const showErrorMessage = () => {
    setMessageInfo({
      severity: "error",
      summary: "Erreur",
      detail: "Une erreur c'est produite , veuillez réessayer!",
    });
    setOpen(true);
  };

  const nameBodyTemplate = (rowData: UserDTO) => {
    return `${rowData.firstName} ${rowData.lastName}`;
  };

  const profilBodyTemplate = (rowData: UserDTO) => {
    return rowData.profil ? `${rowData.profil.name}` : "";
  };

  const accessBodyTemplate = (rowData: UserDTO) => {
    if (rowData.profil?.accesses?.length > 0) {
      return rowData.profil.accesses.map((item) => item.description).join(", ");
    }
    return "Aucune accès";
  };

  const locationBodyTemplate = (rowData: UserDTO) => {
    return rowData.region?.name;
  };

  const handleSearch = () => {
    if (searchTerm.length > 0) {
      setReload((prev: number) => prev + 1);
      dispatch(
        getAllUsers({
          page: 1,
          size: pageSize,
          sortBy: order,
          sortDir: orderDir === 1 ? "asc" : "desc",
          search: searchTerm,
          access: idAccess,
          localisation: localisation,
          profil: idPermission,
        })
      );
    }
  };

  const handleSearchLocalisation = () => {
    if (localisation.length > 0) {
      setReload((prev: number) => prev + 1);
      dispatch(
        getAllUsers({
          page: 1,
          size: pageSize,
          sortBy: order,
          sortDir: orderDir === 1 ? "asc" : "desc",
          search: searchTerm,
          access: idAccess,
          localisation: localisation,
          profil: idPermission,
        })
      );
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (users) {
      const filterValue = e.target.value.toLowerCase();
      setSearchTerm(filterValue);
    }
  };

  const handleCancel = () => {
    setReload(0);
    setSearchTerm("");
    setIdAccess("");
    setLocalisation("");
    setRoleUser(undefined);
    setAccessUser(undefined);
    setIdPermission("");
    dispatch(
      getAllUsers({
        page: 1,
        size: pageSize,
        sortBy: "firstName",
        sortDir: "desc",
        search: "",
        access: "",
        localisation: "",
        profil: "",
      })
    );
  };

  const addingUser = () => navigate("/setting/add-user");
  const goToEditUser = (id: string | { value: string }) => navigate(`/setting/add-user/${getUuidString(id)}`);
  const viewUser = (id: string | { value: string }) => navigate(`/setting/view-detail/${getUuidString(id)}`);

  const confirmDelete = () => {
    if (userDelete && detailsUserConnected) {
      dispatch(
        createUserDeletionRequest({
          userId: getUuidString(userDelete.uuid),
          onError: () => {
            showErrorMessage();
          },
          onSuccess: () => {
            setVisibleConfirmDialog(false);
            setMessageInfo({
              severity: "info",
              summary: "Demande enregistrée",
              detail: `La demande de suppression de l'utilisateur ${userDelete.firstName} est en attente de validation.`,
            });
            setOpen(true);
            dispatch(
              getAllUsers({
                page: currentPage + 1,
                size: pageSize,
                sortBy: order,
                sortDir: orderDir === 1 ? "asc" : "desc",
                search: searchTerm,
                access: idAccess,
                localisation: localisation,
                profil: idPermission,
              })
            );
          },
        })
      );
    }
  };

  const rejectDelete = () => {
    setVisibleConfirmDialog(false);
  };

  const onPageChange = (e: DataTableStateEvent) => {
    setCurrentPage(e.page ?? 0);
    setPageSize(e.rows);
    dispatch(
      getAllUsers({
        page: (e.page ?? 0) + 1,
        size: e.rows,
        sortBy: order,
        sortDir: orderDir === 1 ? "asc" : "desc",
        search: searchTerm,
        access: idAccess,
        localisation: localisation,
        profil: idPermission,
      })
    );
  };

  const actionBodyTemplate = (rowData: UserDTO) => (
    <div className="flex items-center space-x-1">
      <Button
        tooltip="Voir"
        tooltipOptions={{ position: "left" }}
        onClick={() => viewUser(rowData.uuid)}
        icon={<i className="pi pi-eye text-xl text-green-700" />} />

      <Button
        tooltip={
          !isCompetentEntity
            ? "Modifier"
            : "Action réservée aux administrateurs"
        }
        disabled={isCompetentEntity}
        tooltipOptions={{ position: "left", showOnDisabled: true }}
        onClick={() => goToEditUser(rowData.uuid)}
        icon={<i className="pi pi-pen-to-square text-xl text-blue-600" />} />

      <Button
        tooltip={
          !isCompetentEntity
            ? "Supprimer"
            : "Action réservée aux administrateurs"
        }
        disabled={isCompetentEntity}
        tooltipOptions={{ position: "left", showOnDisabled: true }}
        onClick={() => {
          setVisibleConfirmDialog(true);
          if (!rowData.username && rowData.email) {
            setUserDelete({ ...rowData, username: rowData.email });
          } else {
            setUserDelete(rowData);
          }
        }}
        icon={<i className="pi pi-trash text-xl text-red-600" />} />
    </div>
  );

  const paginatorTemplate = useMemo(() => {
    return {
      layout:
        "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
      CurrentPageReport: ({ rows }: PaginatorCurrentPageReportOptions) => {
        const page = currentPage + 1;
        const totalRecords = users?.total ?? 0;
        const first = page * rows + 1 - rows;
        const last = Math.min(first + rows - 1, totalRecords);

        return (
          <span>
            Afficher {first} à {last} sur {totalRecords} lignes
          </span>
        );
      },
    };
  }, [users?.total, currentPage]);
  return (
    <>
      <Message
        open={open}
        severity={messageInfo.severity}
        summary={messageInfo.summary}
        detail={messageInfo.detail}
      />
      <CustomDialog
        visible={visibleConfirmDialog}
        title="Voulez-vous vraiment supprimer cet utilisateur?"
        rejectLabel="Annuler"
        confirmLabel="Supprimer"
        confirmDialog={confirmDelete}
        rejectDialog={rejectDelete}
        loading={loadingDelete === FetchStatusType.LOADING}
        disabled={loadingDelete === FetchStatusType.LOADING}
      />
      <div className="flex flex-col w-full rounded-full">
        <div className="p-4 flex justify-end">
          <Button
            className={classNames(
              "btn-primary bg-green-500 float-right hover:!bg-green-900 text-white font-bold py-2 px-4 rounded-full gap-2",
              { "btn-primary-disabled": isCompetentEntity }
            )}
            onClick={addingUser}
            disabled={isCompetentEntity}
            tooltip={
              !isCompetentEntity ? "" : "Action réservée aux administrateurs"
            }
            tooltipOptions={{ position: "left", showOnDisabled: true }}
          >
            <Plus />
            <span>Ajouter un utilisateur</span>
          </Button>
        </div>
        <div className="flex border-b border-gray-300">
          <div
            onClick={() => setActiveTab('users')}
            className={classNames(
              "flex-1 text-center py-3 cursor-pointer font-bold",
              activeTab === 'users' ? "text-green-700 border-b-4 border-green-700" : "text-gray-500"
            )}
          >
            Gestion des Utilisateurs
          </div>
          <div
            onClick={() => setActiveTab('delete')}
            className={classNames(
              "flex-1 text-center py-3 cursor-pointer font-bold",
              activeTab === 'delete' ? "text-green-700 border-b-4 border-green-700" : "text-gray-500"
            )}
          >
            Demande de Suppression
          </div>
        </div>
        <div className="bg-white p-8 space-y-4 card-container">
          {activeTab === 'users' ? (
            <>
              <div
                className={classNames("grid grid-cols-2 lg:grid-cols-4 gap-5", {
                  "lg:grid-cols-5": isFilter,
                })}
              >
                <div className="flex items-center relative">
                  <InputText
                    placeholder="Nom"
                    className="w-full"
                    onChange={handleInputChange}
                    value={searchTerm}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Button
                    icon="pi pi-search"
                    className="absolute right-0 p-2.5 bg-[#e5e7eb]"
                    onClick={handleSearch}
                  />
                </div>
                <div className="flex items-center">
                  <Dropdown
                    className="w-full"
                    placeholder="Rôles des utilisateurs"
                    options={profiles}
                    optionLabel="name"
                    value={roleUser}
                    onChange={(e) => {
                      setRoleUser(e.value);
                      setIdPermission(e.value.id);
                      dispatch(
                        getAllUsers({
                          page: 1,
                          size: pageSize,
                          sortBy: order,
                          sortDir: orderDir === 1 ? "asc" : "desc",
                          search: searchTerm,
                          access: idAccess,
                          localisation: localisation,
                          profil: e.value.id,
                        })
                      );
                    }}
                  />
                </div>
                <div className="flex items-center relative">
                  <Dropdown
                    className="w-full"
                    placeholder="Accès des utilisateurs"
                    options={listAccess}
                    optionLabel="name"
                    value={accessUser}
                    onChange={(e) => {
                      setAccessUser(e.value);
                      setIdAccess(e.value.id);
                      dispatch(
                        getAllUsers({
                          page: 1,
                          size: pageSize,
                          sortBy: order,
                          sortDir: orderDir === 1 ? "asc" : "desc",
                          search: searchTerm,
                          access: e.value.id,
                          localisation: localisation,
                          profil: idPermission,
                        })
                      );
                    }}
                  />
                </div>
                <div className="flex items-center relative">
                  <InputText
                    value={localisation}
                    placeholder="Localisation"
                    className="w-full"
                    onChange={(e) => setLocalisation(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleSearchLocalisation()
                    }
                  />
                  <Button
                    icon="pi pi-search"
                    className="absolute right-0 p-2.5 bg-[#e5e7eb]"
                    onClick={handleSearchLocalisation}
                  />
                </div>
                {isFilter && (
                  <Button
                    className="btn btn-secondary w-full"
                    onClick={handleCancel}
                  >
                    Annuler
                  </Button>
                )}
              </div>
              <div className="py-4 pl-4 pr-5 justify-content-center items-center relative">
                <DataTable
                  value={
                    isLoading === FetchStatusType.LOADING ? [] : userListFiltered
                  }
                  paginator={
                    isLoading === FetchStatusType.COMPLETED &&
                    (userListFiltered.length || 0) > 0
                  }
                  sortField={order}
                  paginatorTemplate={paginatorTemplate}
                  rows={pageSize}
                  totalRecords={users?.total ?? 0}
                  rowsPerPageOptions={[10, 20, 50]}
                  first={currentPage * pageSize}
                  onPage={onPageChange}
                  sortOrder={orderDir}
                  lazy
                  stripedRows
                  size="small"
                  className="w-full"
                  onSort={(e) => {
                    setOrder(e.sortField);
                    setOrderDir(e.sortOrder ?? 0);
                    dispatch(
                      getAllUsers({
                        page: currentPage + 1,
                        size: pageSize,
                        sortBy: e.sortField,
                        sortDir: (e.sortOrder ?? 0) === 1 ? "asc" : "desc",
                        search: searchTerm,
                        access: idAccess,
                        localisation: localisation,
                        profil: idPermission,
                      })
                    );
                  }}
                  paginatorLeft
                  emptyMessage={
                    isLoading === FetchStatusType.LOADING ? (
                      <div className="flex justify-center py-4">
                        <Spinner />
                      </div>
                    ) : (
                      <div className="flex justify-center py-4">
                        Aucune donnée disponible
                      </div>
                    )
                  }
                >
                  <Column
                    body={nameBodyTemplate}
                    field="firstName"
                    header="Nom"
                    sortable
                  />
                  <Column
                    body={profilBodyTemplate}
                    field="profil"
                    header="Rôle"
                    sortable
                  />
                  <Column
                    body={accessBodyTemplate}
                    field="access"
                    header="Accès"
                    sortable
                  />
                  <Column
                    field="region"
                    body={locationBodyTemplate}
                    header="Localisation"
                    sortable
                  />
                  <Column body={actionBodyTemplate} header="Action" />
                </DataTable>
              </div>
            </>
          ) : (
            <>
              <div className={classNames("grid grid-cols-2 lg:grid-cols-4 gap-5 mb-4", {
                "lg:grid-cols-5": reload > 0 || idAccess.length > 0 || idPermission.length > 0,
              })}>
                <div className="flex items-center relative">
                  <InputText
                    placeholder="Nom"
                    className="w-full"
                    onChange={handleInputChange}
                    value={searchTerm}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Button
                    icon="pi pi-search"
                    className="absolute right-0 p-2.5 bg-[#e5e7eb]"
                    onClick={handleSearch}
                  />
                </div>
                <div className="flex items-center">
                  <Dropdown
                    className="w-full"
                    placeholder="Rôles des utilisateurs"
                    options={profiles}
                    optionLabel="name"
                    value={roleUser}
                    onChange={(e) => {
                      setRoleUser(e.value);
                      setIdPermission(e.value.id);
                      setReload((prev: number) => prev + 1);
                    }}
                  />
                </div>
                <div className="flex items-center relative">
                  <Dropdown
                    className="w-full"
                    placeholder="Accès des utilisateurs"
                    options={listAccess}
                    optionLabel="name"
                    value={accessUser}
                    onChange={(e) => {
                      setAccessUser(e.value);
                      setIdAccess(e.value.id);
                      setReload((prev: number) => prev + 1);
                    }}
                  />
                </div>
                <div className="flex items-center relative">
                  <InputText
                    value={localisation}
                    placeholder="Localisation"
                    className="w-full"
                    onChange={(e) => setLocalisation(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearchLocalisation()}
                  />
                  <Button
                    icon="pi pi-search"
                    className="absolute right-0 p-2.5 bg-[#e5e7eb]"
                    onClick={handleSearchLocalisation}
                  />
                </div>
                {(reload > 0 || idAccess.length > 0 || idPermission.length > 0) && (
                  <Button
                    className="btn btn-secondary w-full"
                    onClick={handleCancel}
                  >
                    Annuler
                  </Button>
                )}
              </div>
              <div className="py-4 pl-4 pr-5 justify-content-center items-center relative">
                <DataTable
                  value={loadingDeleteRequests ? [] : (deleteRequests && Array.isArray(deleteRequests.data) ? deleteRequests.data : [])}
                  paginator={!loadingDeleteRequests && ((deleteRequests?.data?.length || 0) > 0)}
                  sortField={order}
                  paginatorTemplate={deletePaginatorTemplate}
                  paginatorClassName="justify-end flex"
                  rows={deletePageSize}
                  totalRecords={typeof deleteRequests?.total === 'number' ? deleteRequests.total : 0}
                  rowsPerPageOptions={[10, 20, 50]}
                  first={deletePage * deletePageSize}
                  onPage={e => {
                    setDeletePage(e.page ?? 0);
                    setDeletePageSize(e.rows);
                    setReload(prev => prev + 1);
                  }}
                  sortOrder={orderDir}
                  lazy
                  stripedRows
                  size="small"
                  className="w-full"
                  emptyMessage={loadingDeleteRequests ? (
                    <div className="flex justify-center py-4"><Spinner /></div>
                  ) : (
                    <div className="flex justify-center py-4">Aucune demande de suppression</div>
                  )}
                >
                  <Column
                    body={nameBodyTemplate}
                    field="firstName"
                    header="Nom"
                    sortable
                  />
                  <Column
                    body={profilBodyTemplate}
                    field="profil"
                    header="Rôle"
                    sortable
                  />
                  <Column
                    body={accessBodyTemplate}
                    field="access"
                    header="Accès"
                    sortable
                  />
                  <Column
                    field="region"
                    body={locationBodyTemplate}
                    header="Localisation"
                    sortable
                  />
                  <Column body={(rowData: any) => <ActionDeleteRequestTemplate rowData={rowData} onRefresh={() => setReload((prev: number) => prev + 1)} />} header="Action" />
                </DataTable>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ListingUser;
